import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useCart, fmtPrice } from "../contexts/CartContext";
import { useNavigate } from "react-router-dom";
import { ShoppingBag, Trash2, Plus, Minus, ArrowRight, Tag, X, Loader2 } from "lucide-react";
import { BoltIcon } from "./Bolts";
import { ORDER_STORAGE_KEY } from "../data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartButton = ({ className = "" }) => {
    const { totalCount, drawerOpen, setDrawerOpen, items, totalCents, removeItem, updateQty, makeKey, clear } = useCart();
    const navigate = useNavigate();

    const [checkingOut, setCheckingOut] = React.useState(false);
    const [promoInput, setPromoInput] = React.useState("");
    const [promoApplied, setPromoApplied] = React.useState(null); // {code, promo_type, value}
    const [promoLoading, setPromoLoading] = React.useState(false);

    const applyPromo = async () => {
        if (!promoInput.trim()) return;
        setPromoLoading(true);
        try {
            const { data } = await axios.post(`${API}/promo-codes/validate`, null, {
                params: { code: promoInput.trim(), cart_qty: totalCount, cart_subtotal_cents: totalCents }
            });
            setPromoApplied(data);
            setPromoInput("");
            toast.success(`Code "${data.code}" applied!`);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Invalid code.";
            toast.error(msg);
        } finally { setPromoLoading(false); }
    };

    const removePromo = () => { setPromoApplied(null); };

    // Calculate discount for display
    const calcDiscount = () => {
        if (!promoApplied) return { discountCents: 0, shippingWaived: false };
        let disc = 0;
        const shippingWaived = promoApplied.free_shipping || false;
        if (promoApplied.percent_off > 0) disc += Math.round(totalCents * promoApplied.percent_off / 100);
        if (promoApplied.fixed_off_cents > 0) disc += promoApplied.fixed_off_cents;
        if (promoApplied.free_items > 0) {
            const prices = items.flatMap((it) => Array(it.qty).fill(it.priceCents)).sort((a, b) => a - b);
            const freeCount = Math.min(promoApplied.free_items, prices.length);
            for (let i = 0; i < freeCount; i++) disc += prices[i];
        }
        return { discountCents: Math.min(disc, totalCents), shippingWaived };
    };
    const { discountCents, shippingWaived } = calcDiscount();

    // Old behavior — used only as a fallback if Stripe isn't set up yet.
    const fallbackToContactFlow = () => {
        const messageLines = ["ORDER FROM CART", "", ...items.map((it) => {
            const opt = [it.adultSize && `Adult ${it.adultSize}`, it.kidsSize && `Kids ${it.kidsSize}`, it.variant]
                .filter(Boolean)
                .join(" · ");
            return `• ${it.qty}× ${it.title}${opt ? ` (${opt})` : ""} — ${fmtPrice(it.priceCents * it.qty)}`;
        }), "", `SUBTOTAL: ${fmtPrice(totalCents)}`];
        if (promoApplied) messageLines.push(`PROMO: ${promoApplied.code}`);
        if (discountCents > 0) messageLines.push(`DISCOUNT: -${fmtPrice(discountCents)}`);
        if (shippingWaived) messageLines.push("SHIPPING: FREE (promo)");
        messageLines.push("", "Notes / shipping:");
        try {
            sessionStorage.setItem(
                ORDER_STORAGE_KEY,
                JSON.stringify({
                    title: `Cart · ${items.length} item${items.length > 1 ? "s" : ""}`,
                    total: ((totalCents - discountCents) / 100).toFixed(2),
                    message: messageLines.join("\n"),
                })
            );
        } catch {}
        setDrawerOpen(false);
        navigate("/#contact-us-now");
        setTimeout(() => {
            window.dispatchEvent(new Event("dsc:order-placed"));
            document.getElementById("contact-us-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
    };

    // Real checkout — hands off to Stripe's secure, hosted payment page.
    const onCheckout = async () => {
        setCheckingOut(true);
        try {
            const { data } = await axios.post(`${API}/checkout/create-session`, {
                items: items.map((it) => ({
                    title: it.title,
                    priceCents: it.priceCents,
                    qty: it.qty,
                    listing_slug: it.listing_slug || null,
                    adultSize: it.adultSize || null,
                    kidsSize: it.kidsSize || null,
                    variant: it.variant || null,
                })),
                success_url: `${window.location.origin}/order/success`,
                cancel_url: `${window.location.origin}/order/cancelled`,
                promo_code: promoApplied?.code || null,
            });
            window.location.href = data.url;
        } catch (err) {
            if (err?.response?.status === 503) {
                toast.message("Card checkout isn't set up yet — we'll get your order details instead.");
                fallbackToContactFlow();
            } else {
                toast.error("Checkout failed. Please try again, or use Contact Us.");
            }
        } finally {
            setCheckingOut(false);
        }
    };

    return (
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
                <button
                    data-testid="cart-button"
                    aria-label="Open cart"
                    className={`relative inline-flex items-center justify-center gap-2 px-3 py-2 text-white hover:text-storm-bolt transition-colors ${className}`}
                >
                    <ShoppingBag className="w-5 h-5" />
                    {totalCount > 0 && (
                        <span
                            data-testid="cart-count-badge"
                            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center bg-storm-bolt text-black text-[10px] font-heading"
                        >
                            {totalCount}
                        </span>
                    )}
                </button>
            </SheetTrigger>
            <SheetContent
                side="right"
                className="bg-storm-void border-l border-white/10 text-white w-[92vw] sm:w-[440px] p-0 flex flex-col"
            >
                <div className="flex items-center justify-between p-5 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <BoltIcon className="w-4 h-4 text-storm-bolt" />
                        <span className="font-heading text-lg uppercase tracking-wider">Your Cart</span>
                    </div>
                    <span className="text-xs text-storm-mist uppercase tracking-[0.2em]">{totalCount} items</span>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="cart-items">
                    {items.length === 0 ? (
                        <div className="text-center py-14 text-storm-mist">
                            <ShoppingBag className="w-10 h-10 text-storm-mist/30 mx-auto mb-4" />
                            <p className="text-sm">Cart is empty.</p>
                        </div>
                    ) : (
                        items.map((it) => {
                            const key = makeKey(it);
                            return (
                                <div key={key} className="flex gap-3 border border-white/10 bg-storm-navy p-3" data-testid={`cart-item-${key}`}>
                                    {it.imageUrl && (
                                        <img src={it.imageUrl} alt={it.title} className="w-20 h-20 object-cover flex-shrink-0 bg-storm-void" />
                                    )}
                                    <div className="flex-1 min-w-0 flex flex-col">
                                        <div className="flex items-start justify-between gap-2">
                                            <h4 className="font-heading text-sm uppercase tracking-wider text-white line-clamp-2">{it.title}</h4>
                                            <button onClick={() => removeItem(key)} className="text-storm-mist hover:text-destructive" aria-label="Remove">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                        {(it.adultSize || it.kidsSize || it.variant) && (
                                            <p className="text-[11px] text-storm-mist mt-0.5">
                                                {[it.adultSize && `Adult ${it.adultSize}`, it.kidsSize && `Kids ${it.kidsSize}`, it.variant].filter(Boolean).join(" · ")}
                                            </p>
                                        )}
                                        <div className="flex items-center justify-between mt-auto pt-2">
                                            <div className="flex items-center border border-white/10">
                                                <button onClick={() => updateQty(key, it.qty - 1)} className="w-7 h-7 inline-flex items-center justify-center text-storm-mist hover:text-storm-bolt" aria-label="Decrease">
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="w-7 text-center text-sm">{it.qty}</span>
                                                <button onClick={() => updateQty(key, it.qty + 1)} className="w-7 h-7 inline-flex items-center justify-center text-storm-mist hover:text-storm-bolt" aria-label="Increase">
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                            </div>
                                            <span className="font-heading text-storm-bolt">{fmtPrice(it.priceCents * it.qty)}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {items.length > 0 && (
                    <div className="p-5 border-t border-white/10 space-y-3">
                        {/* Promo code */}
                        {promoApplied ? (
                            <div className="flex items-center justify-between bg-storm-navy border border-storm-bolt/30 px-3 py-2">
                                <div className="flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-storm-bolt" />
                                    <span className="text-sm text-white font-heading uppercase">{promoApplied.code}</span>
                                    <span className="text-xs text-storm-bolt">
                                        {[
                                            promoApplied.free_shipping && "Free shipping",
                                            promoApplied.percent_off > 0 && `${promoApplied.percent_off}% off`,
                                            promoApplied.fixed_off_cents > 0 && `${fmtPrice(promoApplied.fixed_off_cents)} off`,
                                            promoApplied.free_items > 0 && `${promoApplied.free_items} free item${promoApplied.free_items > 1 ? "s" : ""}`,
                                        ].filter(Boolean).join(" + ")}
                                    </span>
                                </div>
                                <button onClick={removePromo} className="text-storm-mist hover:text-red-400"><X className="w-4 h-4" /></button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input value={promoInput} onChange={(e) => setPromoInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") applyPromo(); }}
                                    placeholder="Promo code"
                                    className="flex-1 bg-storm-void border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-storm-bolt uppercase placeholder:normal-case" />
                                <button onClick={applyPromo} disabled={promoLoading || !promoInput.trim()}
                                    className="text-xs font-heading uppercase tracking-wider text-storm-bolt border border-storm-bolt/30 px-3 py-2 hover:bg-storm-bolt hover:text-black transition-colors disabled:opacity-50">
                                    {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                                </button>
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <span className="eyebrow">Subtotal</span>
                            <span className="font-heading text-lg text-white">{fmtPrice(totalCents)}</span>
                        </div>

                        {discountCents > 0 && (
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-storm-bolt uppercase tracking-wider">Discount</span>
                                <span className="font-heading text-storm-bolt">-{fmtPrice(discountCents)}</span>
                            </div>
                        )}
                        {shippingWaived && (
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-storm-bolt uppercase tracking-wider">Shipping</span>
                                <span className="font-heading text-storm-bolt">FREE</span>
                            </div>
                        )}

                        {(discountCents > 0) && (
                            <div className="flex items-center justify-between border-t border-white/10 pt-2">
                                <span className="eyebrow">New Total</span>
                                <span className="font-heading text-2xl text-white">{fmtPrice(totalCents - discountCents)}</span>
                            </div>
                        )}

                        <p className="text-[11px] text-storm-mist leading-relaxed">
                            {shippingWaived ? "Shipping waived. " : "Shipping & "}taxes calculated at checkout. Secure payment by card.
                        </p>
                        <button
                            onClick={onCheckout}
                            disabled={checkingOut}
                            data-testid="cart-checkout"
                            className="btn-primary w-full justify-center disabled:opacity-60"
                        >
                            {checkingOut ? "Redirecting…" : <><ArrowRight className="w-4 h-4" /> Checkout</>}
                        </button>
                        <button onClick={clear} className="w-full text-xs uppercase tracking-[0.25em] text-storm-mist hover:text-storm-bolt py-2">
                            Clear cart
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};

export default CartButton;
